// web-v2/src/lib/verifyFire.js
//
// A CONFIRMATION MUST BE A READ, NOT AN ECHO. Prompt 106 item 2.
//
// David: "I fired a quote using the IAT API. It toasted a quote number and a
// premium, but it doesn't appear anywhere... how would anyone know?"
//
// The answer was that nobody could. The toast reported the parsed SOAP response
// — the object the function had just built — while the persistence ran
// fire-and-forget behind a .catch(). So the message announced an INTENTION, and
// on the one fire where the write landed without a carrier_id it announced one
// that the ladder could not show.
//
// THE READ-BACK QUERIES THE STORE THE LADDER QUERIES. Reading back the object
// you just sent proves nothing; reading back the endpoint the ladder renders
// from proves the row is there AND joinable, which are two different things and
// both of them failed once.
//
// AN EMPTY READ SAYS IT DID NOT LAND. Not a warning, not a silence — the plain
// statement, because that is the case where David needs to act.

import { getQuotesForDealPanel } from '@/services/quotes.service.js';

/**
 * Read the fired quote back out of the ladder's own source.
 *
 * @param {string} dealId
 * @param {object} opts
 *   quoteId     the id the fire reported persisting, when it reported one
 *   carrierRefId the carriers.id to fall back to when it did not
 * @returns {Promise<{landed: boolean, quote: object|null, reason: string|null}>}
 *   landed:false with a reason is a RESULT, not an error. The fire may have
 *   succeeded at the carrier and failed to store, and those need different
 *   words.
 */
export async function verifyFiredQuote(dealId, { quoteId = null, carrierRefId = null } = {}) {
  let rows;
  try {
    const r = await getQuotesForDealPanel(dealId);
    rows = r?.quotations || r?.quotes || [];
  } catch (e) {
    // THE READ ITSELF FAILED, which is not the same as the quote being absent.
    // Saying "it did not land" here would invent a loss out of a network blip —
    // the empty-result-versus-failed-call rule, on the surface that reports it.
    return { landed: false, quote: null, reason: `could not check: ${e.message}` };
  }
  const byId = quoteId ? rows.find((q) => String(q.id) === String(quoteId)) : null;
  if (byId) return { landed: true, quote: byId, reason: null };
  // The id is the strong match. The carrier is the fallback for a fire that
  // reported no id, and it is deliberately the RESOLVED id rather than a display
  // name: three code paths are free to rewrite the name.
  const byCarrier = carrierRefId != null
    ? rows.filter((q) => String(q.carrier_ref_id) === String(carrierRefId))
      .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')))[0]
    : null;
  if (byCarrier) return { landed: true, quote: byCarrier, reason: null };
  return {
    landed: false,
    quote: null,
    reason: quoteId
      ? 'the quote was saved but the ladder cannot read it back'
      : 'nothing was saved',
  };
}
